import { omitProperties } from "application/src/app-utils/omit-property.ts";
import { useCallback, useMemo } from "react";
import type { Key } from "react-aria";
import type { DraggableItem } from "widgets";
import {
  Card,
  Disclosure,
  HStack,
  Item,
  MenuOptionItem,
  MenuTriggerButton,
  ReorderableList,
  ReorderableListItem,
  Section,
  Switch,
  SymbolIcon,
  VStack,
} from "widgets";

import { refreshMain } from "../../main-canvas/render-main.ts";
import { PileupRenderingModeEnum, TrackEnum } from "../../openapi";
import { useGenomeViewerStore } from "../../store/store.ts";
import { type TrackGroup } from "../../types.ts";
import { TracksConfigurationDialog } from "../track-configuration/TracksConfiguration.tsx";
import { TrackGroupConfigurationDialog } from "./TrackGroupConfigurationDialog.tsx";
import style from "./TrackGroupsList.module.css";
import { TrackList } from "./TracksList.tsx";

type TrackGroupsWithID = { id: string; name: string } & TrackGroup;

export const TrackGroupsListCard = () => {
  return (
    <Card
      noBorder
      title="Tracks"
      scrollable
      actions={[<OptionsMenu key={`options-menu`} />, <TracksConfigurationDialog key={`track-configuration-dialog`} />]}
    >
      <TrackGroupsList />
    </Card>
  );
};

export const TrackGroupsList = () => {
  const trackGroups = useGenomeViewerStore((state) => state.trackGroups);
  const setTrackGroupVisible = useGenomeViewerStore((state) => state.setTrackGroupVisible);
  const changeTrackGroupPosition = useGenomeViewerStore((state) => state.changeTrackGroupPosition);
  const tracks = useGenomeViewerStore((state) => state.tracks);

  // Transform the Map into an array of objects with the correct shape for our component
  const trackGroupsArray: TrackGroupsWithID[] = useMemo(() => {
    return Array.from(trackGroups.entries()).map(([groupId, trackGroup]) => ({
      id: groupId,
      name: trackGroup.contigName,
      ...trackGroup,
    }));

    // We need to react on track changes as well, e.g. when changing the track visibility
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackGroups, tracks]);

  const handleReorder = useCallback(
    (items: TrackGroupsWithID[]) => {
      const newTrackGroups = items.map((item: DraggableItem<TrackGroup>) => {
        // remove id and name from the DraggableItem props
        return [item.id, omitProperties(item, ["id", "name"]) as TrackGroup] as [string, TrackGroup];
      });

      changeTrackGroupPosition(new Map<string, TrackGroup>(newTrackGroups));
    },
    [changeTrackGroupPosition],
  );

  return (
    <ReorderableList<TrackGroupsWithID>
      items={trackGroupsArray}
      onReorder={handleReorder}
      renderDragPreview={renderDragPreview}
      key={"reorderable-track-group-list"}
      dropIndicatorClassName={style.drop_indicator}
    >
      {(trackGroup) => {
        const groupId = trackGroup.id;
        const listedTracks = trackGroup.trackIds
          .map((trackId) => tracks.get(trackId))
          .filter((track) => track != undefined);

        return (
          <ReorderableListItem id={groupId} key={`track-group-item-${groupId}`} className={style.group_list_item}>
            <VStack className={style.stack}>
              <Disclosure
                className={style.item}
                header={
                  <VStack spacing={1}>
                    <div className={style.title}>{trackGroup.organismName}</div>
                    {!!trackGroup.contigName && <div className={style.subtitle}>{trackGroup.contigName}</div>}
                  </VStack>
                }
                actions={[
                  <HStack key={`track-group-action-${groupId}`}>
                    <TrackGroupConfigurationDialog groupId={groupId} />
                    <Switch
                      isSelected={trackGroup.graphics.isVisible}
                      onChange={(isSelected) => setTrackGroupVisible(groupId, isSelected)}
                      aria-label={"Show track group"}
                    />
                  </HStack>,
                ]}
                data-e2e-label={"tracks"}
                defaultOpen
              >
                <TrackList groupId={groupId} tracks={listedTracks} key={`tracks-list-group-${groupId}`} />
              </Disclosure>
            </VStack>
            <div className={style.line} />
          </ReorderableListItem>
        );
      }}
    </ReorderableList>
  );
};

const renderDragPreview = (trackGroup: TrackGroup) => (
  <div style={{ width: "100%", opacity: 0.9 }}>
    <VStack>
      <div className={style.title}>{trackGroup.organismName}</div>
      {!!trackGroup.contigName && <div className={style.subtitle}>{trackGroup.contigName}</div>}
    </VStack>
  </div>
);

const OptionsMenu = () => {
  const pileupRenderingMode = useGenomeViewerStore((state) => state.pileupRenderingMode);
  const setPileupRenderingMode = useGenomeViewerStore((state) => state.setPileupRenderingMode);
  const showTrackHeaders = useGenomeViewerStore((state) => state.showTrackHeaders);
  const toggleTrackHeaderVisibility = useGenomeViewerStore((state) => state.toggleTrackHeaderVisibility);

  const hasProteinTracks = useGenomeViewerStore((state) =>
    [...state.tracks.values()].some((t) => t.trackType === TrackEnum.PROTEIN_LOCATION),
  );

  const handleAction = (key: Key) => {
    switch (key) {
      case "expanded-pileup": {
        setPileupRenderingMode(PileupRenderingModeEnum.EXPANDED);
        break;
      }
      case "condensed-pileup": {
        setPileupRenderingMode(PileupRenderingModeEnum.CONDENSED);
        break;
      }
      case "show-tracks-label": {
        toggleTrackHeaderVisibility();
        break;
      }
    }

    refreshMain();
  };

  return (
    <MenuTriggerButton icon="more_vert" label="More" onAction={handleAction}>
      <Section title="View">
        <Item key="show-tracks-label">
          <MenuOptionItem
            title={"Show Track Labels"}
            icon="label"
            tag={showTrackHeaders ? <SymbolIcon icon="check" /> : undefined}
          />
        </Item>
      </Section>
      {hasProteinTracks ? (
        <Section title="Protein Tracks">
          <Item key="expanded-pileup">
            <MenuOptionItem
              title="Expanded"
              icon="expand"
              tag={pileupRenderingMode === PileupRenderingModeEnum.EXPANDED ? <SymbolIcon icon="check" /> : undefined}
            />
          </Item>
          <Item key="condensed-pileup">
            <MenuOptionItem
              title="Collapsed"
              icon="compress"
              tag={pileupRenderingMode === PileupRenderingModeEnum.CONDENSED ? <SymbolIcon icon="check" /> : undefined}
            />
          </Item>
        </Section>
      ) : null}
    </MenuTriggerButton>
  );
};
